<template>
  <div class="quota-add-view">
    <!-- Breadcrumb & Header -->
    <div class="mb-4">
      <div class="d-flex align-items-center gap-2 text-muted small mb-2">
        <router-link :to="{ name: 'Quota' }" class="text-muted text-decoration-none">
          {{ $t('quota.title') }}
        </router-link>
        <span>/</span>
        <span class="text-white">{{ $t('quota.expansionTitle') }}</span>
      </div>
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('quota.expansionTitle') }}</h2>
          <p class="text-muted small mb-0">
            {{ $t('quota.expansionSubtitle') }}
          </p>
        </div>
        <router-link :to="{ name: 'Quota' }" class="btn btn-outline-secondary d-flex align-items-center gap-2">
          <i class="mdi mdi-arrow-left"></i>
          <span>{{ $t('common.back') }}</span>
        </router-link>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="row g-4">
      <!-- Left Column: Configuration Form -->
      <div class="col-lg-7">
        <div class="card card-glass p-4 mb-4">
          <h5 class="text-white font-weight-bold mb-3 d-flex align-items-center gap-2">
            <i class="mdi mdi-domain text-warning"></i>
            <span>{{ $t('quota.selectClient') }}</span>
          </h5>

          <!-- Company Select -->
          <div class="mb-4">
            <label class="form-label text-light small font-weight-bold">{{ $t('quota.clientCompany') }}</label>
            <select
              v-model="selectedCompanyId"
              class="form-select bg-dark text-white border-secondary"
              @change="onCompanyChange"
              :disabled="loadingCompanies"
            >
              <option value="" disabled>{{ $t('quota.chooseClientPlaceholder') }}</option>
              <option v-for="c in companies" :key="c.id" :value="c.id">
                {{ (c.company && c.company.name) || c.name }} ({{ $t('quota.activeQuota') }}: {{ (c.company && c.company.number_of_players) || 100 }} {{ $t('quota.seats') }})
              </option>
            </select>
          </div>

          <!-- Current Quota Telemetry Strip -->
          <div v-if="selectedCompany" class="p-3 rounded mb-4" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08);">
            <div class="row text-center">
              <div class="col-4 border-end border-secondary">
                <div class="text-muted small">{{ $t('quota.activeQuota') }}</div>
                <div class="h5 font-weight-bold text-white mb-0">{{ currentCompanyQuota }} {{ $t('quota.seats') }}</div>
              </div>
              <div class="col-4 border-end border-secondary">
                <div class="text-muted small">{{ $t('quota.registeredPlayers') }}</div>
                <div class="h5 font-weight-bold text-info mb-0">{{ selectedCompany.players_count || 0 }}</div>
              </div>
              <div class="col-4">
                <div class="text-muted small">{{ $t('quota.newQuotaEstimate') }}</div>
                <div class="h5 font-weight-bold text-success mb-0">{{ newCalculatedQuota }} {{ $t('quota.seats') }}</div>
              </div>
            </div>
          </div>

          <h5 class="text-white font-weight-bold mb-3 d-flex align-items-center gap-2">
            <i class="mdi mdi-seat-passenger text-primary"></i>
            <span>{{ $t('quota.additionalSeatsCount') }}</span>
          </h5>

          <!-- Preset Chips -->
          <div class="d-flex gap-2 mb-3 flex-wrap">
            <button
              v-for="preset in [10, 25, 50, 100, 200]"
              :key="preset"
              type="button"
              class="btn flex-fill py-2"
              :class="additionalSeats === preset ? 'btn-primary' : 'btn-outline-secondary'"
              @click="additionalSeats = preset"
            >
              +{{ preset }} {{ $t('quota.seats') }}
            </button>
          </div>

          <!-- Custom Seat Input -->
          <div class="mb-4">
            <label class="form-label text-light small font-weight-bold">{{ $t('quota.customSeatsLabel') }}</label>
            <div class="input-group">
              <input
                v-model.number="additionalSeats"
                type="number"
                min="1"
                max="2000"
                class="form-control bg-dark text-white border-secondary"
                :placeholder="$t('quota.customSeatsPlaceholder')"
              />
              <span class="input-group-text bg-dark text-muted border-secondary">{{ $t('quota.playerSeats') }}</span>
            </div>
          </div>

          <h5 class="text-white font-weight-bold mb-3 d-flex align-items-center gap-2">
            <i class="mdi mdi-calendar-clock text-cyan"></i>
            <span>{{ $t('quota.licenseDuration') }}</span>
          </h5>

          <!-- Duration Selector -->
          <div class="row g-2 mb-4">
            <div class="col-6 col-sm-3" v-for="d in durationOptions" :key="d.months">
              <button
                type="button"
                class="btn w-100 p-2 text-center"
                :class="selectedDuration === d.months ? 'btn-primary' : 'btn-outline-secondary'"
                @click="selectedDuration = d.months"
              >
                <div class="font-weight-bold">{{ d.months }} {{ d.months === 1 ? $t('register.monthUnit') : $t('register.monthsUnit') }}</div>
                <small v-if="d.discountRate" class="text-warning d-block" style="font-size: 10px;">
                  {{ d.discountRate * 100 }}% OFF
                </small>
                <small v-else class="text-muted d-block" style="font-size: 10px;">{{ $t('quota.standard') }}</small>
              </button>
            </div>
          </div>

          <!-- Voucher Code Input -->
          <div>
            <label class="form-label text-light small font-weight-bold">{{ $t('quota.voucherCode') }}</label>
            <div class="input-group">
              <input
                v-model.trim="voucherCode"
                type="text"
                class="form-control bg-dark text-white border-secondary text-uppercase"
                :placeholder="$t('quota.voucherPlaceholder')"
              />
              <button
                type="button"
                class="btn btn-outline-warning"
                @click="applyVoucher"
                :disabled="!voucherCode"
              >
                {{ $t('quota.applyVoucher') }}
              </button>
            </div>
            <div v-if="discountApplied" class="text-success small mt-1 d-flex align-items-center gap-1">
              <i class="mdi mdi-check-circle"></i> {{ $t('quota.voucherApplied') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Order Summary & Execution -->
      <div class="col-lg-5">
        <div class="card card-glass p-4 sticky-top" style="top: 2rem;">
          <h5 class="text-white font-weight-bold mb-3 d-flex align-items-center gap-2">
            <i class="mdi mdi-receipt text-success"></i>
            <span>{{ $t('quota.quotaSummary') }}</span>
          </h5>

          <div class="summary-list py-2 border-top border-bottom border-secondary mb-3">
            <div class="d-flex justify-content-between py-2 text-muted small">
              <span>{{ $t('quota.clientCompany') }}</span>
              <span class="text-white font-weight-medium">
                {{ selectedCompany?.company?.name || selectedCompany?.name || '-' }}
              </span>
            </div>
            <div class="d-flex justify-content-between py-2 text-muted small">
              <span>{{ $t('checkout.pricePerUnit') }}</span>
              <span class="text-white font-weight-medium">Rp {{ formatPrice(pricePerSeatPerMonth) }}</span>
            </div>
            <div class="d-flex justify-content-between py-2 text-muted small">
              <span>{{ $t('quota.additionalSeatsCount') }}</span>
              <span class="text-info font-weight-bold">+{{ additionalSeats || 0 }} {{ $t('quota.seats') }}</span>
            </div>
            <div class="d-flex justify-content-between py-2 text-muted small">
              <span>{{ $t('quota.licenseDuration') }}</span>
              <span class="text-white font-weight-medium">{{ selectedDuration }} {{ $t('billing.month') }}</span>
            </div>
            <div class="d-flex justify-content-between py-2 text-muted small">
              <span>{{ $t('quota.seatsSubtotal') }}</span>
              <span class="text-white font-weight-medium">Rp {{ formatPrice(rawSubtotal) }}</span>
            </div>
            <div v-if="discountAmount > 0" class="d-flex justify-content-between py-2 text-success small">
              <span>{{ $t('quota.voucherCode') }}</span>
              <span>- Rp {{ formatPrice(discountAmount) }}</span>
            </div>
            <div class="d-flex justify-content-between py-2 text-muted small">
              <span>{{ $t('quota.taxPpn') }}</span>
              <span class="text-white font-weight-medium">Rp {{ formatPrice(taxAmount) }}</span>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mb-4">
            <span class="text-white font-weight-bold">{{ $t('quota.totalPayable') }}</span>
            <div class="text-end">
              <div class="h3 font-weight-bold text-warning mb-0">Rp {{ formatPrice(grandTotal) }}</div>
              <small class="text-muted">{{ $t('quota.taxPpn') }}</small>
            </div>
          </div>

          <div class="alert alert-info py-2 px-3 small border-0 mb-4" style="background: rgba(6, 182, 212, 0.1); color: #67e8f9;">
            <i class="mdi mdi-shield-check mr-1"></i>
            {{ $t('quota.newQuotaEstimate') }}: <strong>{{ newCalculatedQuota }} {{ $t('quota.seats') }}</strong>.
          </div>

          <button
            type="button"
            class="btn btn-primary btn-lg w-100 d-flex align-items-center justify-content-center gap-2 mb-2"
            @click="submitAddQuota"
            :disabled="submitting || !selectedCompanyId || additionalSeats <= 0"
          >
            <span v-if="submitting" class="spinner-border spinner-border-sm" role="status"></span>
            <i v-else class="mdi mdi-check-circle-outline font-size-18"></i>
            <span>{{ $t('quota.proceedToPayment') }}</span>
          </button>

          <router-link
            :to="{ name: 'Quota' }"
            class="btn btn-outline-secondary w-100"
          >
            {{ $t('common.cancel') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config/config';
import store from '../store';
import Swal from 'sweetalert2';

export default {
  name: 'QuotaAdd',
  data() {
    return {
      companies: [],
      selectedCompanyId: '',
      loadingCompanies: true,
      submitting: false,
      additionalSeats: 25,
      selectedDuration: 1,
      pricePerSeatPerMonth: 30000,
      voucherCode: '',
      discountPercent: 0,
      discountApplied: false,
      durationOptions: [
        { months: 1, discountRate: 0 },
        { months: 3, discountRate: 0.05 },
        { months: 6, discountRate: 0.10 },
        { months: 12, discountRate: 0.15 }
      ]
    };
  },
  computed: {
    selectedCompany() {
      return this.companies.find((c) => c.id === this.selectedCompanyId) || null;
    },
    currentCompanyQuota() {
      if (!this.selectedCompany) return 0;
      return parseInt(this.selectedCompany.company?.number_of_players || 100);
    },
    newCalculatedQuota() {
      return this.currentCompanyQuota + (parseInt(this.additionalSeats) || 0);
    },
    rawSubtotal() {
      const seats = parseInt(this.additionalSeats) || 0;
      return seats * this.pricePerSeatPerMonth * this.selectedDuration;
    },
    discountAmount() {
      let durationDiscount = 0;
      if (this.selectedDuration === 3) durationDiscount = 0.05;
      if (this.selectedDuration === 6) durationDiscount = 0.10;
      if (this.selectedDuration === 12) durationDiscount = 0.15;

      const voucherDiscount = this.discountApplied ? (this.discountPercent / 100) : 0;
      const totalDiscountRate = Math.min(0.5, durationDiscount + voucherDiscount);
      return Math.round(this.rawSubtotal * totalDiscountRate);
    },
    taxAmount() {
      const taxableAmount = Math.max(0, this.rawSubtotal - this.discountAmount);
      return Math.round(taxableAmount * 0.11);
    },
    grandTotal() {
      return Math.max(0, this.rawSubtotal - this.discountAmount) + this.taxAmount;
    }
  },
  mounted() {
    this.fetchCompanies();
  },
  methods: {
    formatPrice(val) {
      if (!val) return '0';
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },
    async fetchCompanies() {
      this.loadingCompanies = true;
      try {
        const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
        let resp;
        try {
          resp = await axios.get(`${config.API_URL}company`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        } catch (fallbackErr) {
          resp = await axios.get(`${config.API_URL}company-list`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
        const list = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
        this.companies = Array.isArray(list) ? list : [];
        if (this.companies.length > 0) {
          this.selectedCompanyId = this.companies[0].id;
        }
      } catch (err) {
        console.error('Failed to load companies:', err);
      } finally {
        this.loadingCompanies = false;
      }
    },
    onCompanyChange() {
      // Trigger reactive update
    },
    applyVoucher() {
      const isEn = this.$currentLocale === 'en';
      const code = (this.voucherCode || '').toUpperCase().trim();
      if (code === 'GAMITEAM2026' || code === 'PROMO20') {
        this.discountPercent = 20;
        this.discountApplied = true;
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: isEn ? '20% Promo voucher applied!' : 'Kupon 20% berhasil diterapkan!',
          showConfirmButton: false,
          timer: 2500,
          background: '#0f172a',
          color: '#fff'
        });
      } else if (code === 'EARLYBIRD100' || code === 'SPECIAL10') {
        this.discountPercent = 10;
        this.discountApplied = true;
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: isEn ? '10% Promo voucher applied!' : 'Kupon 10% berhasil diterapkan!',
          showConfirmButton: false,
          timer: 2500,
          background: '#0f172a',
          color: '#fff'
        });
      } else {
        this.discountApplied = false;
        Swal.fire({
          icon: 'warning',
          title: isEn ? 'Invalid Voucher' : 'Kupon Tidak Valid',
          text: isEn ? 'The voucher code entered was not found or has expired.' : 'Kode voucher diskon yang dimasukkan tidak ditemukan atau sudah kadaluarsa.',
          background: '#0f172a',
          color: '#fff'
        });
      }
    },
    async submitAddQuota() {
      if (!this.selectedCompanyId || this.additionalSeats <= 0) return;
      const isEn = this.$currentLocale === 'en';
      const compName = this.selectedCompany?.company?.name || 'Client';

      const confirm = await Swal.fire({
        title: isEn ? 'Confirm Quota Allocation' : 'Konfirmasi Penambahan Kuota',
        text: isEn 
          ? `Add ${this.additionalSeats} seats to ${compName}? New capacity will be ${this.newCalculatedQuota} seats.`
          : `Tambahkan ${this.additionalSeats} kursi ke ${compName}? Kuota baru akan menjadi ${this.newCalculatedQuota} kursi.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#475569',
        confirmButtonText: isEn ? 'Yes, Confirm Top-up' : 'Ya, Konfirmasi Top-up',
        cancelButtonText: isEn ? 'Cancel' : 'Batal',
        background: '#0f172a',
        color: '#fff'
      });

      if (!confirm.isConfirmed) return;

      this.submitting = true;
      try {
        const token = store.state.authToken || localStorage.getItem('authToken');
        const newTotal = this.newCalculatedQuota;

        const billingForm = new FormData();
        billingForm.append('package_id', 1);
        billingForm.append('duration', this.selectedDuration);
        billingForm.append('additional_user', this.additionalSeats);
        billingForm.append('additional_user_duration', this.selectedDuration);
        billingForm.append('disc_code', this.voucherCode || '');
        billingForm.append('company_id', this.selectedCompanyId);
        billingForm.append('_method', 'POST');

        let createdInvoice = null;
        try {
          const billResp = await axios.post(`${config.API_URL}billing/store`, billingForm, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          });
          createdInvoice = billResp.data?.data?.billing_transaction || null;
          if (createdInvoice) {
            sessionStorage.setItem('noinvoice', createdInvoice.invoice_number || '');
            sessionStorage.setItem('amount', createdInvoice.amount || this.grandTotal);
            sessionStorage.setItem('billing_id', createdInvoice.billing_id || '');
          }
        } catch (billingErr) {
          console.warn('Billing invoice creation fallback to direct quota allocation:', billingErr?.response?.data || billingErr.message);
        }

        await axios.put(`${config.API_URL}company/${this.selectedCompanyId}`, {
          numberOfPlayers: newTotal,
          number_of_players: newTotal
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        await Swal.fire({
          icon: 'success',
          title: isEn ? 'Quota Top-up Successful!' : 'Top-up Kuota Berhasil!',
          text: isEn 
            ? `Seat capacity for ${compName} has been increased to ${newTotal} player seats.`
            : `Kapasitas kursi ${compName} telah ditingkatkan menjadi ${newTotal} pemain.`,
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#10b981'
        });

        if (createdInvoice && createdInvoice.billing_id) {
          this.$router.push({ path: '/billing/detail' });
        } else {
          this.$router.push({ name: 'Quota' });
        }
      } catch (err) {
        console.error('Error adding quota:', err);
        Swal.fire({
          icon: 'error',
          title: isEn ? 'Failed to Add Quota' : 'Gagal Menambahkan Kuota',
          text: err.response?.data?.error || err.response?.data?.message || (isEn ? 'A system error occurred while updating quota allocation.' : 'Terjadi kesalahan sistem saat memperbarui alokasi kuota.'),
          background: '#0f172a',
          color: '#fff'
        });
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.quota-add-view {
  color: #e2e8f0;
}

.card-glass {
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
}
</style>